import { Box, ButtonBase, useTheme } from "@mui/material";
import { createSxStyles } from "@common/createSxStyles";

export enum RiskGovernanceMode {
    Current = 0,
    AfterMitigation = 1,
}

interface RiskGovernanceModeSwitcherProps {
    selected: RiskGovernanceMode;
    onSelect: (mode: RiskGovernanceMode) => void;
    onOpenSnapshot: () => void;
    currentLabel: string;
    afterMitigationLabel: string;
    snapshotLabel: string;
    // True while a point-in-time snapshot is shown — neither Current nor After mitigation is the active view then.
    snapshotActive?: boolean;
    // Disabled while viewing a point-in-time snapshot — no after-mitigation projection is reconstructed for the past.
    afterMitigationDisabled?: boolean;
    afterMitigationDisabledTooltip?: string;
}

// The governance-overview mode switcher: a bare row of pills sitting in the filter bar, flipping the overview
// between its current state, the projected "after mitigation" state, and a point-in-time snapshot.
function RiskGovernanceModeSwitcher({
    selected,
    onSelect,
    onOpenSnapshot,
    currentLabel,
    afterMitigationLabel,
    snapshotLabel,
    snapshotActive,
    afterMitigationDisabled,
    afterMitigationDisabledTooltip,
}: RiskGovernanceModeSwitcherProps) {
    const styles = useStyles();

    return (
        <Box sx={styles.group}>
            <ButtonBase
                sx={[styles.button, selected === RiskGovernanceMode.Current && !snapshotActive && styles.selected]}
                onClick={() => onSelect(RiskGovernanceMode.Current)}
            >
                {currentLabel}
            </ButtonBase>
            <ButtonBase
                sx={[
                    styles.button,
                    selected === RiskGovernanceMode.AfterMitigation && !snapshotActive && !afterMitigationDisabled && styles.selected,
                    !!afterMitigationDisabled && styles.disabled,
                ]}
                disabled={afterMitigationDisabled}
                title={afterMitigationDisabled ? afterMitigationDisabledTooltip : undefined}
                onClick={() => onSelect(RiskGovernanceMode.AfterMitigation)}
            >
                {afterMitigationLabel}
            </ButtonBase>
            <ButtonBase sx={[styles.button, !!snapshotActive && styles.selected]} onClick={onOpenSnapshot}>
                {snapshotLabel}
            </ButtonBase>
        </Box>
    );
}

const useStyles = () => {
    const theme = useTheme();
    return createSxStyles({
        group: {
            width: "fit-content",
            alignSelf: "flex-start",
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(0.5),
            padding: theme.spacing(0.75),
        },
        button: {
            paddingX: theme.spacing(2.5),
            paddingY: theme.spacing(1),
            borderRadius: "14px",
            fontWeight: theme.typography.fontWeightBold,
            fontSize: "0.85rem",
            color: theme.palette.grey[500],
            transition: "background-color 0.15s ease, color 0.15s ease",
            "&:hover": {
                backgroundColor: theme.palette.grey[100],
            },
        },
        selected: {
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.dark,
            outline: "2px solid " + theme.palette.primary.main,
            outlineOffset: "-2px",
            "&:hover": {
                backgroundColor: theme.palette.primary.light,
            },
        },
        disabled: {
            opacity: 0.4,
            cursor: "not-allowed",
            "&:hover": {
                backgroundColor: "transparent",
            },
        },
    });
};

export default RiskGovernanceModeSwitcher;
